import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Notification visual variants — mirror Figma "Flashka Design System" file
 * `YZK9r3wDhvw1pY60KlVEIj`, "Notifications" section (node `1997:17374`).
 *
 * Reference nodes (Compact / Title+Avatar band, one per state):
 *   - 2013:10839 – Compact / Unread        (bg primary-50 + trailing dot)
 *   - 2013:11095 – Compact / Read          (bg secondary-50, no dot)
 *   - 2013:11399 – Compact / Hover         (read surface)
 *   - 2013:11513 – Compact / Pressed       (read surface)
 *   - 2013:11323 – Compact / Focus (read)  (1px primary-900 ring + white@70%)
 *   - 2013:10962 – Compact / Focus (unread)(1px primary-900 ring + primary-50@50%)
 *   - 2013:11209 – Compact / Disabled      (opacity-70)
 *   - 2013:10880 – Compact / Title+Icon    (22px leading icon)
 * Anatomy bands:
 *   - 2013:9702  – Default (title + description + timestamp + action row)
 *   - 2013:11627 – Large   (Default + media block)
 *
 * Axes:
 *   size: compact | default | large   (Figma "content")
 *   read: true (Read) | false (Unread)
 *   disabled: true | false
 *
 * Surface model — a SINGLE element carries bg + radius + padding. Figma draws
 * the focus state as a 1px outer border with a 1px gap (`p-px`), but the
 * codebase standard (Button / Toast / MenuListItem) renders focus as a flush
 * `web:focus-visible:ring-1 ring-offset-0` ring — visually identical to a 1px
 * solid border hugging the rounded edge, and the only thing that survives the
 * native crossing (RN has no keyboard focus / `ring-*`). So we use the ring
 * here too, plus the per-state translucent fill swap on the same focused
 * element (a two-box `focus-within` split breaks because the focused element
 * is the box's PARENT, not a descendant).
 *
 * State → surface bg (read vs unread):
 *   ┌──────────┬───────────────────────┬────────────────────────┐
 *   │ state    │ Read (no dot)         │ Unread (dot)           │
 *   ├──────────┼───────────────────────┼────────────────────────┤
 *   │ rest     │ secondary-50 (#fff)   │ primary-50  (#f5f3ff)  │
 *   │ hover    │ secondary-100         │ primary-100            │
 *   │ pressed  │ secondary-200         │ primary-200            │
 *   │ focus    │ ring prim-900 +       │ ring prim-900 +        │
 *   │          │ secondary-50/70       │ primary-50/50          │
 *   │ disabled │ opacity-70 (whole card, node 2013:11209)       │
 *   └──────────┴───────────────────────┴────────────────────────┘
 *
 * VISIBILITY BUMP — Figma's literal read hover/pressed nodes resolve to
 * secondary-50 (hover) / secondary-100 (pressed), but `secondary-50` is
 * `#ffffff` (= the rest surface) so the hover would be invisible. Per the
 * established MenuListItem precedent we shift the interactive ladder up one
 * step (hover → -100, pressed → -200) for perceptibility. The Unread ladder
 * (primary 50 → 100 → 200) was confirmed with product (Figma has no
 * unread-hover/pressed node), and lands on the same +1 shape.
 *
 * Dark mode: the palette auto-inverts via CSS vars (`:root` / `.dark:root`,
 * generated from `brand-colors.json`), so `bg-primary-50` renders the dark
 * violet tint with no manual swap. The ONE exception is the Read surface: the
 * card sits on the glass `secondary-100` overlay (the bell popover), and a
 * mirrored `secondary-50` is the darkest surface step — the row would sink
 * BELOW its host into a near-black hole. In light the row sits one step ABOVE
 * the overlay (white on #f5f5f5), so dark keeps that relation one step up the
 * ramp: rest `secondary-200`, hover/pressed `secondary-300`. `dark:`-only, so
 * light (and the light-only mobile app) is unchanged. The non-inverting mids
 * (`primary-500` dot, `primary-200` media border) stay fixed by intent.
 *
 * Interaction classes are gated `enabled:` — on the non-pressable (static
 * `View`) card they no-op (a `<div>` has no `:enabled`), so a read-only feed
 * row correctly shows no hover/press/focus. `web:` keeps focus utilities off
 * native (NativeWind v4 would otherwise apply `ring-*` unconditionally and
 * throw on RN). Transitions are `web:`-gated for the same RN 0.83 + Hermes
 * reason as Button/MenuListItem.
 */
export const notificationVariants = cva(
  // gap-[12px] enforces the Figma 12px vertical rhythm between the top
  // row, an optional media block, and the action row — without it the
  // action pill sits flush against the timestamp (Figma node 29429:21203
  // shows 12px between the timestamp and the "View Ranking" pill).
  'group relative flex flex-col gap-[12px] rounded-[16px] px-[16px] py-[12px] ' +
    'web:transition-colors web:duration-150 web:ease-out ' +
    'web:focus-visible:ring-1 web:focus-visible:ring-offset-0 ' +
    'web:focus-visible:ring-primary-900',
  {
    variants: {
      read: {
        // Read — neutral secondary ladder.
        true:
          'bg-surface-raised ' +
          'enabled:hover:bg-secondary-100 enabled:active:bg-secondary-200 ' +
          'web:focus-visible:bg-secondary-50/70 ' +
          // Dark: one step ABOVE the overlay host, never below it (see note).
          // Interaction swaps are `web:`-gated: dark mode is web-only and the
          // stacked variants stay out of NativeWind's interop layer.
          'dark:bg-secondary-100 web:dark:enabled:hover:bg-secondary-200 ' +
          'web:dark:enabled:active:bg-secondary-300 web:dark:focus-visible:bg-secondary-200/70',
        // Unread — deepen within the primary scale (product decision).
        false:
          'bg-primary-50 ' +
          'enabled:hover:bg-primary-100 enabled:active:bg-primary-200 ' +
          'web:focus-visible:bg-primary-50/50',
      },
      // Density. Padding + radius are constant across all three sizes — the
      // real difference is STRUCTURAL (which child rows mount: compact has no
      // description; media/actions render when their props are passed). The
      // axis stays so the type is honest and a future per-size tweak has a home.
      size: {
        compact: '',
        default: '',
        large: '',
      },
      disabled: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // Disabled — keep the surface, fade the whole card to 70% (Figma node
      // 2013:11209). Note: this component's disabled is opacity-70, NOT the
      // Button/MenuListItem opacity-30 — matches its own Figma spec.
      { disabled: true, class: '!opacity-70' },
    ],
    defaultVariants: { read: false, size: 'default', disabled: false },
  },
);

/**
 * Title — Figma H4 (Mona SemiBold 16/24).
 *
 * Color switches on `read`:
 *   - unread → `primary-950` (Figma node 29429:21203)
 *   - read   → `secondary-950` (subtitle-800 equivalent, Figma node 29429:21227)
 *
 * The purple tint is EXCLUSIVE to the unread state — the read row is the
 * quiet neutral variant. Passing `read` at render time keeps the two states
 * distinguishable at the container-color level even when the surface fill is
 * transparent.
 */
export const notificationTitleVariants = cva(
  'text-left text-h4 font-semibold native:font-mona-semibold',
  {
    variants: {
      read: {
        true: 'text-secondary-950',
        false: 'text-primary-950',
      },
    },
    defaultVariants: { read: false },
  },
);

/**
 * Description — Figma B2 (Mona Medium 14/22).
 *
 * - unread → `primary-950 @ 50%`
 * - read   → `secondary-600` (100%)
 */
export const notificationDescriptionVariants = cva(
  'text-left text-b2 font-medium native:font-mona-medium',
  {
    variants: {
      read: {
        true: 'text-secondary-600',
        false: 'text-primary-950/50',
      },
    },
    defaultVariants: { read: false },
  },
);

/**
 * Timestamp — Figma B3 (Mona Medium 12/20).
 *
 * - unread → `primary-950 @ 50%`
 * - read   → `secondary-500`
 */
export const notificationTimestampVariants = cva(
  'text-left text-b3 font-medium native:font-mona-medium',
  {
    variants: {
      read: {
        true: 'text-fg-subtle',
        false: 'text-primary-950/50',
      },
    },
    defaultVariants: { read: false },
  },
);

/**
 * Shared {@link Button} size for both action buttons — Figma action chips are
 * 36px tall = Button `s` (h-9). The primary action's bevel + inset highlight
 * is exactly Button `primary`; the secondary is Button `secondary`.
 */
export const ACTION_BUTTON_SIZE = 's' as const;
export const PRIMARY_ACTION_VARIANT = 'primary' as const;
export const SECONDARY_ACTION_VARIANT = 'secondary' as const;

export type NotificationVariantProps = VariantProps<typeof notificationVariants>;
